import type { InventoryResource } from '@repo/api-gw-sdk';
import type {
  Column,
  PaginationState,
  SortingState,
} from '@tanstack/react-table';
import { useState } from 'react';

import { securityAssignmentStatus } from '@/components/queryBuilder/properties';
import { Table } from '@/components/table';
import { useColumns } from '@/components/table/hooks';
import { useDAL } from '@/contexts/useDAL';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { type Condition } from '@/types/advanceFilter';

import InventoryMultiInstanceMenu from './InventoryMultiInstanceMenu';
import {
  inventoryListTableColumns,
  inventoryListTableHiddenColumns,
} from './inventoryListTableColumns';

interface InventoryListTableProps {
  showFiltersBar: boolean;
  sorting: SortingState;
  selectedItems?: InventoryResource[];
  pagination: PaginationState;
  query?: Condition;
  disableSelection?: boolean;
  onSortingChange: (sortField: string, desc: boolean) => void;
  onPaginationChange: (value: PaginationState) => void;
  onRowSelectionChange?: (
    entities: InventoryResource[],
    onRowContentChange?: () => Promise<void>
  ) => void;
  paginationBarActions?: ((props: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    columns: Column<any>[];
  }) => React.ReactElement)[];
}

export const InventoryListTable = (props: InventoryListTableProps) => {
  const dal = useDAL();
  const [selectedEntities, setSelectedEntities] = useState<InventoryResource[]>(
    []
  );

  const {
    body,
    isLoading,
    mutate: mutateAllInventory,
  } = dal.inventory.list(props.pagination, props.sorting[0], props.query);

  const { securityScan } = useFeatureFlags();
  const filteredColumns = inventoryListTableColumns.filter(
    (column) => securityScan || column.name !== securityAssignmentStatus.name
  );

  const columns = useColumns<InventoryResource>(filteredColumns);

  const onRowSelectionChange = (entities: InventoryResource[]) => {
    setSelectedEntities(entities);
    props.onRowSelectionChange?.(entities, async () => {
      await mutateAllInventory();
    });
  };

  return (
    <Table
      entityLabel='resources'
      disableSelection={props.disableSelection}
      showFiltersBar={props.showFiltersBar}
      data={body?.resources || []}
      testId='inventory-table'
      isMultiSelect={true}
      multiSelectActionButton={
        <InventoryMultiInstanceMenu entities={selectedEntities} />
      }
      columns={columns}
      hiddenColumns={inventoryListTableHiddenColumns.map((x) => x.name)}
      stickyHeader
      // pinnedColumns={[backupStatus.name, resourceName.name]}
      sorting={props.sorting}
      isLoading={isLoading}
      onSortingChanged={(state) =>
        props.onSortingChange(state[0].id, state[0].desc)
      }
      onRowSelectionChange={onRowSelectionChange}
      pagination={{
        ...props.pagination,
        recordsCount: body?.pagination.recordsCount,
        onChange: props.onPaginationChange,
      }}
      selectedItems={props.selectedItems}
      paginationBarActions={props.paginationBarActions}
    />
  );
};
